import { Info } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { IndicatorsCollectionManagement } from 'lib/api/collections/types';
import CustomButton from 'lib/designSystem/Button';
import CentralPieChart, {
  CentralPieChartItem,
} from 'lib/designSystem/Charts/CentralPieChart';
import ColoredCircle from 'lib/designSystem/ColoredCircle';
import EnhancedTable from 'lib/designSystem/Table';
import { CHART_COLORS } from 'lib/functions/const/colors';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

type ManagementsDialogProps = {
  openDialog: boolean;
  handleDialogClose: () => void;
  managementsForInsights: IndicatorsCollectionManagement[];
  selectedSegment: CentralPieChartItem;
};

const ManagementsDialog = ({
  openDialog,
  handleDialogClose,
  managementsForInsights,
  selectedSegment,
}: ManagementsDialogProps) => {
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState<CentralPieChartItem | null>(
    null,
  );

  const filteredManagementsForInsights = selectedItem
    ? managementsForInsights.filter(
        (x: IndicatorsCollectionManagement) =>
          x.collectorName === selectedItem.name,
      )
    : managementsForInsights;

  const handleShowBusinessDetails = (id: number) => {
    const row = managementsForInsights.find(
      (x: IndicatorsCollectionManagement) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: number) => {
    const row = managementsForInsights.find(
      (x: IndicatorsCollectionManagement) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier ?? '',
        name: row.stakeholderName ?? '',
      }),
    );
  };

  const handlePieChartClick = (entry: CentralPieChartItem) => {
    setSelectedItem(entry);
  };

  const managementsForInsightsChartDataSource = managementsForInsights.reduce(
    (
      acc: Record<string, number>,
      management: IndicatorsCollectionManagement,
    ) => {
      const collectorName = management.collectorName;
      if (acc[collectorName]) {
        acc[collectorName] = acc[collectorName] + 1;
      } else {
        acc[collectorName] = 1;
      }
      return acc;
    },
    {},
  );

  const managementsForInsightsChartData = Object.entries(
    managementsForInsightsChartDataSource,
  ).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="xl">
      <DialogTitle>
        <ColoredCircle
          color={selectedSegment?.color ?? CHART_COLORS.green}
          size={15}
        />
        {` ${selectedSegment?.name}${selectedItem ? ` - ${selectedItem.name}` : ''}`}
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={3} sm={3}>
              <CentralPieChart
                data={managementsForInsightsChartData}
                showLegend={false}
                height={300}
                onClick={handlePieChartClick}
              />
            </Grid>
            <Grid item xs={9} sm={9}>
              <EnhancedTable
                dataHeaders={[
                  { key: 'id', label: 'ID', type: 'numeric-id' },
                  { key: 'collectorName', label: 'Ejecutivo' },
                  { key: 'type', label: 'Tipo' },
                  { key: 'folio', label: 'Folio' },
                  {
                    key: 'businessName',
                    label: 'Cliente',
                    handleAction: handleShowBusinessDetails,
                    icon: <Info fontSize="small" />,
                  },
                  {
                    key: 'stakeholderName',
                    label: 'Deudor',
                    handleAction: handleShowStakeholderDetails,
                    icon: <Info fontSize="small" />,
                  },
                  { key: 'dueDate', label: 'Vencimiento', type: 'date' },
                  { key: 'createdAt', label: 'Creado', type: 'date' },
                ]}
                data={filteredManagementsForInsights}
                exportToFile="xlsx"
              />
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleDialogClose}>Cerrar</CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ManagementsDialog;
